import { v4 as uuid } from "uuid";
import { DefaultMap, defined } from "@glideapps/ts-necessities";

import {
    Database,
    DataReaderWriter,
    Transaction,
    DocumentData,
    isSizeLimitException,
    builderAppDocumentFromParts,
    DataWriter,
    loadBuilderApp,
} from "./Database";
import { isOlderThan, nullToUndefined } from "./Support";
import { logInfo, logError } from "./debug-print";
import { AppDescription } from "./components/SerializedApp";
import { builderAppsCollectionName, isSample, builderAppLocksCollectionName } from "./database-strings";
import { SyncJobQueue } from "./sync-job-queue";
import { getFeatureSetting } from "./feature-settings";

const stealWithoutAskingAfter = 3 * 60 * 1000;
export const timeBetweenRefresh = 30 * 1000;

const lastSessionIDs: Map<string, string> = new Map();
const lastSessionIDKeyPrefix = "lastSessionID-";

const appJobQueues = new DefaultMap<string, SyncJobQueue>(() => new SyncJobQueue());

function getLastSessionID(appID: string): string | undefined {
    let id = lastSessionIDs.get(appID);
    if (id === undefined && sessionStorage !== undefined) {
        id = nullToUndefined(sessionStorage.getItem(lastSessionIDKeyPrefix + appID));
    }
    return id;
}

function setLastSessionID(appID: string, id: string): void {
    logInfo("session ID saving", appID, id);
    lastSessionIDs.set(appID, id);
    if (sessionStorage !== undefined) {
        sessionStorage.setItem(lastSessionIDKeyPrefix + appID, id);
    }
}

async function forceBuilderAppLockInner(db: DataReaderWriter, userID: string, appID: string): Promise<string> {
    const sessionID = uuid();
    logInfo("session ID updating to", appID, sessionID);
    await db.updateDocument(builderAppLocksCollectionName, appID, {
        sessionID,
        ownerUserID: userID,
        lastSeenAt: db.database.serverTimestampFieldValue,
    });
    logInfo("session ID updated to", appID, sessionID);
    return sessionID;
}

export async function forceBuilderAppLock(db: Database, userID: string, appID: string): Promise<void> {
    return await appJobQueues
        .get(appID)
        .run(async () => setLastSessionID(appID, await forceBuilderAppLockInner(db, userID, appID)));
}

async function updateLastFailedAt(db: DataReaderWriter, userID: string, appID: string): Promise<void> {
    // FIXME: This is a hack in multiple ways.  First, Firestore forces us
    // to write this document, since we've read it, which is required in
    // client transactions.  The second hack is that our security rules
    // require us to set the `ownerUserID`.
    await db.updateDocument(builderAppLocksCollectionName, appID, {
        ownerUserID: userID,
        lastFailedAt: db.database.serverTimestampFieldValue,
    });
}

export enum LockResult {
    SelfOwned,
    CurrentOtherOwned,
    OldOtherOwned,
    Free,
    Error,
}

// If this returns a `LockResult`, the caller transaction must write to the
// lock document, or Firestore won't be happy.  If it returns a string, then
// that's the session ID of the successful lock.
async function tryLockBuilderAppInner(
    tx: Transaction,
    userID: string,
    appID: string
): Promise<
    | string
    | {
          result: LockResult.CurrentOtherOwned | LockResult.OldOtherOwned | LockResult.Free;
      }
> {
    logInfo("getting lock document", appID, userID);
    const doc = await tx.getDocument(builderAppLocksCollectionName, appID);
    const lastID = getLastSessionID(appID);
    logInfo("lock document", doc, lastID);
    if (doc === undefined || (lastID !== undefined && doc.sessionID === lastID)) {
        return await forceBuilderAppLockInner(tx, userID, appID);
    }

    if (doc.sessionID === undefined) {
        return { result: LockResult.Free };
    } else {
        const lastSeenAt = tx.database.dateFromTimestamp(doc.lastSeenAt);
        if (isOlderThan(lastSeenAt, stealWithoutAskingAfter)) {
            return { result: LockResult.OldOtherOwned };
        } else {
            return { result: LockResult.CurrentOtherOwned };
        }
    }
}

export async function tryLockBuilderApp<T extends boolean>(
    db: Database,
    userID: string,
    appID: string,
    takeLockIfFreeOrOld: T
): Promise<{
    result: T extends true ? LockResult.SelfOwned | LockResult.CurrentOtherOwned | LockResult.Error : LockResult;
}> {
    return await appJobQueues.get(appID).run(async () => {
        try {
            const result = await db.runTransaction("tryLockBuilderApp", async tx => {
                const r = await tryLockBuilderAppInner(tx, userID, appID);
                if (typeof r === "string") {
                    return r;
                }
                if (takeLockIfFreeOrOld && (r.result === LockResult.Free || r.result === LockResult.OldOtherOwned)) {
                    return await forceBuilderAppLockInner(tx, userID, appID);
                } else {
                    await updateLastFailedAt(tx, userID, appID);
                    return r;
                }
            });
            if (typeof result === "string") {
                setLastSessionID(appID, result);
                return { result: LockResult.SelfOwned as any };
            }
            // We're doing this in this roundabout way because we have to cast the inner
            // result to `any`, and this way at least we get a little bit of type checking.
            return {
                result: result.result as any,
            };
        } catch (e: unknown) {
            logError("Could not try lock builder app", e);
            return { result: LockResult.Error as any };
        }
    });
}

async function forceUnlockBuilderAppInner(tx: DataReaderWriter, userID: string, appID: string): Promise<void> {
    await tx.updateDocument(builderAppLocksCollectionName, appID, {
        sessionID: tx.database.deleteFieldValue,
        ownerUserID: userID,
    });
}

async function unlockBuilderAppInner(tx: DataReaderWriter, userID: string, appID: string): Promise<void> {
    const doc = await tx.getDocument(builderAppLocksCollectionName, appID);
    const lastID = getLastSessionID(appID);
    if (doc === undefined) return;
    if (doc.sessionID === lastID) {
        await forceUnlockBuilderAppInner(tx, userID, appID);
    } else {
        await updateLastFailedAt(tx, userID, appID);
    }
}

export async function unlockBuilderApp(db: Database, userID: string, appID: string): Promise<void> {
    if (isSample(appID)) return;

    return await appJobQueues.get(appID).run(
        async () =>
            await db.runTransaction("unlockBuilderApp", async tx => {
                await unlockBuilderAppInner(tx, userID, appID);
            })
    );
}

export async function createBuilderApp(
    db: DataWriter,
    appDescription: AppDescription,
    maybeAppID: string | undefined,
    userID: string
): Promise<string> {
    const dbApp = builderAppDocumentFromParts(
        db.database,
        appDescription,
        getFeatureSetting("base64EncodeBuilderApp"),
        userID,
        // We're assuming that serials start at 0; this will be +1 inside the
        // resulting dbApp.
        -1,
        false
    );

    try {
        return await db.setDocument(builderAppsCollectionName, maybeAppID, dbApp);
    } catch (e: unknown) {
        logError("Creating builder app threw", maybeAppID, e);
        throw e;
    }
}

async function handleBuilderAppSavingException(
    db: Database,
    appID: string,
    dbApp: DocumentData,
    e: any
): Promise<void> {
    if (isSizeLimitException(e)) throw e;

    try {
        const failedPath = `${builderAppsCollectionName}/${appID}/failed`;
        const doc: DocumentData = {
            failedAt: db.serverTimestampFieldValue,
            builderApp: dbApp,
        };
        if (typeof e.message === "string") {
            doc.errorMessage = e.message;
        }
        if (typeof e.stack === "string") {
            doc.stack = e.stack;
        }
        await db.setDocument(failedPath, undefined, doc);
    } catch {
        logError("Saving failed builder app save failed");
    }
}

export enum UpdateBuilderAppResultKind {
    Success,
    NoLock,
    SizeLimit,
    UnknownFailure,
    StaleSerial,
}

export type UpdateBuilderAppResult =
    | {
          kind: UpdateBuilderAppResultKind;
      }
    | {
          kind: UpdateBuilderAppResultKind.Success;
          nextSerial: number;
          sessionID: string;
      }
    | {
          kind: UpdateBuilderAppResultKind.UnknownFailure;
          exception: unknown;
      };

export function isSuccessfulUpdateBuilderAppResult(
    x: UpdateBuilderAppResult
): x is {
    kind: UpdateBuilderAppResultKind.Success;
    nextSerial: number;
    sessionID: string;
} {
    return x.kind === UpdateBuilderAppResultKind.Success;
}

export function isUnknownFailureBuilderAppResult(
    x: UpdateBuilderAppResult
): x is {
    kind: UpdateBuilderAppResultKind.UnknownFailure;
    exception: unknown;
} {
    return x.kind === UpdateBuilderAppResultKind.UnknownFailure;
}

export async function updateBuilderApp(
    db: Database,
    appID: string,
    // Since this has to happen in a "lock", we need to capture the exact
    // application state at the time of application, or else we run into all
    // sorts of additional concurrency problems. But, even though this is
    // pretty much _only_ Redux, we can't use Redux here because this is
    // app/src/common, which sometimes is referred to in the functions.
    getCurrentApplicationState: (appID: string) => { appDescription: AppDescription; userID: string; serial: number },
    // Getting the application state through Redux means we also have to be
    // responsible for updating the next serial in Redux. Again, since we're
    // in app/src/common we can't directly address anything in Redux.
    updateSerial: (appID: string, nextSerial: number) => void
): Promise<UpdateBuilderAppResult> {
    // If we didn't use a sync queue for saving,
    // 1. It would be possible for two transactions to run concurrently.  One would win, thereby stealing the session ID
    //    from the other.
    // 2. We would lose data when trying to save the builder app: the publish code path would, at one point,
    //    capture stale Redux state and overwrite actual builder changes.
    return await appJobQueues.get(appID).run(async () => {
        const { appDescription, userID, serial } = getCurrentApplicationState(appID);
        const dbApp: DocumentData = builderAppDocumentFromParts(
            db,
            appDescription,
            getFeatureSetting("base64EncodeBuilderApp"),
            userID,
            serial
        );

        try {
            const result = await db.runTransaction("updateBuilderApp", async tx => {
                const prior = await loadBuilderApp(tx, appID);
                if (prior?.serial !== undefined && prior.serial !== serial) {
                    return { kind: UpdateBuilderAppResultKind.StaleSerial };
                }

                const sessionID = await tryLockBuilderAppInner(tx, userID, appID);
                logInfo("tried locking", appID, sessionID);
                if (typeof sessionID !== "string") {
                    await updateLastFailedAt(tx, userID, appID);
                    return { kind: UpdateBuilderAppResultKind.NoLock };
                }

                // Why do this "with nesting"? This is an unfortunate layering violation, but a useful one:
                //
                // db.updateDocument() usually calls Firestore .set(), which will create documents if
                // they do not already exist.
                // db.updateDocumentWithNesting() always calls Firestore .update(), which will fail if
                // the target document does not already exist.
                //
                // We want to ensure this is only ever called on existing documents.
                // FIXME: We shouldn't be incurring layering violations like this.
                await tx.updateDocumentWithNesting(builderAppsCollectionName, appID, dbApp);

                return { kind: UpdateBuilderAppResultKind.Success, sessionID, nextSerial: dbApp.serial };
            });
            if (result.kind === UpdateBuilderAppResultKind.Success) {
                setLastSessionID(appID, defined(result.sessionID));
                updateSerial(appID, defined(result.nextSerial));
            }
            return result;
        } catch (e: unknown) {
            logError("Updating builder app threw", appID, e);
            await handleBuilderAppSavingException(db, appID, dbApp, e);
            if (isSizeLimitException(e)) return { kind: UpdateBuilderAppResultKind.SizeLimit };
            return { kind: UpdateBuilderAppResultKind.UnknownFailure, exception: e };
        }
    });
}
